import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Grid, Cell } from 'formlabs-web-components/Grid';
import { Header } from 'formlabs-web-components/Type';
import { Button } from 'formlabs-web-components/Link';
import { Trans, useTranslation } from 'react-i18next';
import { analytics } from '../../utils/index';

import Modal from '../content/Modal';
import './NewsLetterSignUp.module.css';

import {
  FormSubmit,
  MarketoForm,
  Message,
  Input,
  Select,
  PresetSelect,
  PhoneInput
} from '../form-builder';

const track = eventName =>
  analytics.track(
    eventName,
    {
      category: 'Segment'
    },
    {
      integrations: {
        Marketo: false,
        'Marketo V2': false
      }
    }
  );

const PreformDownloadModal = ({ platform }) => {
  const [submitted, setSubmitted] = useState(false);
  const [hasNewProduct, setHasNewProduct] = useState(false);

  const { t } = useTranslation();

  const downloadPreform = () => {
    setSubmitted(true);
    const url = `https://formlabs.com/download-preform-${platform.toLowerCase()}`;
    if (window) {
      window.location.href = url;
    }
    track('PreForm Downloaded');
  };

  const updateNewProduct = e => {
    setHasNewProduct(
      e.target.value ===
        'Just got a Formlabs product, using PreForm for the first time' ||
        e.target.value === 'Existing user upgrading or re-downloading PreForm'
    );
  };

  const formProps = {
    name: 'Preform_Download',
    errorMessage: t(
      'There was an error submitting your request. Please try again!'
    ),
    successCallback: downloadPreform,
    successMessage: t('Thanks for Downloading PreForm'),
    trackingLabel: 'Preform Download Form Submitted',
    analyticsTrackMapping: { email: 'Email' },
    analyticsIdentifyMapping: {
      userId: 'Email',
      traits: {
        email: 'Email',
        firstName: 'FirstName',
        lastName: 'LastName',
        phone: 'Phone'
      }
    },
    marketoOptions: {
      munchkinId: '060-UIG-504',
      baseUrl: 'https://app-sj15.marketo.com',
      formId: '1041'
    }
  };

  return (
    <Fragment>
      {!submitted && (
        <MarketoForm {...formProps} keepModalOpen>
          <Grid direction="x" gutters="margin">
            <Cell mediumSize={24}>
              <Header level={4} displayAs={4} weight="400">
                {t('Download PreForm')}
              </Header>
              <p>
                <strong>
                  {t('Fill out the form below to begin downloading PreForm')}
                </strong>
              </p>
            </Cell>
          </Grid>
          <Grid direction="x" gutters="margin">
            <Cell mediumSize={12}>
              <Input
                name="FirstName"
                label={t('First Name')}
                type="text"
                isRequired
              />
            </Cell>

            <Cell mediumSize={12}>
              <Input
                name="LastName"
                label={t('Last Name')}
                type="text"
                isRequired
              />
            </Cell>
          </Grid>
          <Input
            name="Email"
            label={t('Business email')}
            type="email"
            isRequired
          />
          <PhoneInput name="Phone" label={t('Phone')} isRequired />
          <Grid direction="x" gutters="margin">
            <Cell mediumSize={24}>
              <PresetSelect
                name="How_would_you_describe_yourself__c"
                label={t('How Would You Describe Yourself')}
                type="occupation"
                isRequired
              />
            </Cell>
            <Cell mediumSize={24}>
              <PresetSelect
                name="Country"
                secondaryName="State"
                label={t('Country')}
                type="countryAndUsState"
                isRequired
              />
            </Cell>

            <Cell mediumSize={24}>
              <Select
                name="How_do_you_plan_to_use_PreForm__c"
                label={t('How do you plan to use PreForm?')}
                isRequired
                onChange={updateNewProduct}
              >
                <option value="">– {t('Select')} –</option>
                <option value="Testing it out">{t('Testing it out')}</option>
                <option value="Just got a Formlabs product, using PreForm for the first time">
                  {t(
                    'Just got a Formlabs product, using PreForm for the first time'
                  )}
                </option>
                <option value="Existing user upgrading or re-downloading PreForm">
                  {t('Existing user upgrading or re-downloading PreForm')}
                </option>
              </Select>
            </Cell>

            {hasNewProduct && (
              <Cell mediumSize={24}>
                <Select
                  name="Where_do_you_buy_your_Formlabs_products__c"
                  label={t('Where do you buy your Formlabs products?')}
                  isRequired
                >
                  <option value="">– {t('Select')} –</option>
                  <option value="Directly from Formlabs">
                    {t('Formlabs')}
                  </option>
                  <option value="From a reseller">{t('Resellers')}</option>
                  <option value="Sometimes directly from Formlabs, sometimes from a reseller">
                    {t('Both')}
                  </option>
                </Select>
              </Cell>
            )}
          </Grid>

          <Grid direction="x" gutters="margin">
            <Cell mediumSize={12}>
              <FormSubmit label={t('Download PreForm')} />
            </Cell>
            <Cell mediumSize={12} secondaryAlignment="align-self-middle">
              <Message type="error" />
              <Message type="success" />
            </Cell>
          </Grid>
        </MarketoForm>
      )}
      {submitted && (
        <Fragment>
          <Header displayAs={2} level={2} weight={600}>
            {t('Thanks for Downloading PreForm')}
          </Header>
          <p>
            <Trans>
              Your download should start soon. If it doesn't please click{' '}
              <a
                href={`https://formlabs.com/download-preform-${platform.toLowerCase()}`}
              >
                here
              </a>
            </Trans>
          </p>
        </Fragment>
      )}
    </Fragment>
  );
};

const PreformForm = DisplayComponent(PreformDownloadModal);

const PreformDownloadButton = ({ classes, platform, query, email }) => {
  const { t } = useTranslation();

  const openModalCallback = () => {
    track('PreForm Download Request');
  };

  if (query === 'update' || email)
    return (
      <Button
        className={classes}
        color={platform === 'Windows' ? 'dark' : 'light'}
        to={`https://formlabs.com/download-preform-${platform.toLowerCase()}`}
        target="_blank"
      >
        {t('Download PreForm ({{platform}})', { platform })}
      </Button>
    );

  return (
    <Modal
      id="preform-download"
      className={classes}
      color={platform === 'Windows' ? 'dark' : 'light'}
      label={t('Download PreForm ({{platform}})', { platform })}
      width="600px"
      openModalCallback={openModalCallback}
    >
      <PreformForm platform={platform} />
    </Modal>
  );
};

const PreformDownload = DisplayComponent(PreformDownloadButton);

export default connect(state => {
  return {
    email: state.auth.email
  };
})(PreformDownload);
